import { ChangeDetectorRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImmersionCourseService } from '../immersion-course.service';
import { GenericModalService } from '../generic-modal/generic-modal.service';
import { TranslatePipe } from '../translate.pipe';
import { CourseContentType } from '../types/course-content-type';
import { ActivityProgress } from './activity-progress';
import { IframeActivityMessageDto } from './iframe-activity-message-dto';
import { IframeActivityModalData } from './iframe-activity-modal-data';
import {
  DEFAULT_IFRAME_ACTIVITY_MODAL_HEIGHT,
  isActivityProgress,
  toHeight,
  toOrigin,
} from './iframe-activity-modal.helpers';

// Angular does not inherit @Component metadata: each subclass keeps its own decorator, template and scss
export abstract class IframeActivityModalBase implements OnInit, OnDestroy {
  readonly url$: Observable<string>;
  height: number = DEFAULT_IFRAME_ACTIVITY_MODAL_HEIGHT;
  targetOrigin: string;

  protected abstract readonly closeEventType: string;
  protected abstract readonly heightEventType: string;
  protected abstract readonly reportActivityProgressEventType: string;
  protected abstract readonly courseContentType: CourseContentType;
  // true when the embedded app reports progress only as it closes: it is past its own UI by then
  protected readonly closeOnSaveError: boolean = false;

  private readonly urlSubject$ = new BehaviorSubject<string>(null);
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly activeModal: NgbActiveModal,
    private readonly cdRef: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly immersionCourseService: ImmersionCourseService,
    private readonly genericModalService: GenericModalService,
    private readonly translatePipe: TranslatePipe,
    readonly modalData: IframeActivityModalData
  ) {
    this.url$ = this.urlSubject$.asObservable();
  }

  ngOnInit(): void {
    if (this.getHost()) {
      this.urlSubject$.next(this.buildUrl());
    }
    this.targetOrigin = toOrigin(this.getHost());
    // every window message would otherwise start an app-wide change detection; handleEvents re-enters on real change
    this.ngZone.runOutsideAngular(() => window.addEventListener('message', this.messageListener));
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageListener);
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeModal(): void {
    this.activeModal.close();
  }

  handleEvents({ data, origin }: IframeActivityMessageDto): void {
    const hostOrigin = toOrigin(this.getHost());
    if (!hostOrigin || hostOrigin !== origin) {
      return;
    }

    if (!data || !data.type) {
      return;
    }

    const { type, data: eventData } = data;
    switch (type) {
      case this.closeEventType:
        this.ngZone.run(() => this.closeModal());
        break;
      case this.heightEventType:
        this.height = toHeight(eventData);
        this.cdRef.detectChanges();
        break;
      case this.reportActivityProgressEventType:
        if (isActivityProgress(eventData)) {
          this.ngZone.run(() => this.reportActivityProgress(eventData));
        }
        break;
      default:
        break;
    }
  }

  protected abstract getHost(): string;

  // the safeUrl bypass trusts this URL: its host is configured in OeAppService and every query value is encoded
  protected abstract buildUrl(): string;

  private readonly messageListener = (event: IframeActivityMessageDto): void => this.handleEvents(event);

  private reportActivityProgress({ timeOnTask, completed }: ActivityProgress): void {
    // replays (e.g. from the activity calendar) opt out so they don't record a new activity
    if (this.modalData.reportProgress === false) {
      this.closeModal();
      return;
    }
    this.immersionCourseService
      .savePersonCourse(timeOnTask, completed, this.modalData.item.id, this.courseContentType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeModal();
        },
        error: () => {
          if (this.closeOnSaveError) {
            this.closeModal();
          }
          this.genericModalService.show(
            this.translatePipe.transform('alert.titleError'),
            this.translatePipe.transform('errors.unablePerformAction')
          );
        },
      });
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/iframe-activity-modal/iframe-activity-modal.base.ts